import { useEffect, useState } from 'react';
import { listini } from '../../api/listini';
import type { CorrispondenzaLetta } from '../../api/listini';

/*  Da un nome scritto nei file al fornitore di Mago che intendiamo noi.

    È una tabella corta — un nome per distributore — e sta qui invece che fra le
    impostazioni perché il momento in cui serve è uno solo: si legge in griglia «COMINOX
    non è fra le corrispondenze», si scrive la riga, si rilancia la passata. Metterla in
    un'altra schermata vorrebbe dire far attraversare l'applicativo a chi ha appena letto
    il motivo.

    Sulla sigla vale la pena essere chiari, perché la stessa casella significa due cose
    diverse a seconda del file:

    – nel modello che mandiamo ai fornitori la sigla NON c'è, e quella scritta qui è
      quella che vale per tutte le righe di quel file;
    – nel catalogo di un grossista la sigla sta dentro il file, colonna «Categoria», e
      quella scritta qui non viene nemmeno guardata.

    Resta obbligatoria in entrambi i casi perché la tabella è una sola. È il motivo per
    cui ABBATTISTA porta un «ABB» che non significa niente, e il modulo lo dice invece di
    lasciarlo scoprire.
*/
export function Corrispondenze({
  modificabili,
  onChiudi,
}: {
  modificabili: boolean;
  onChiudi: () => void;
}) {
  const [righe, setRighe] = useState<CorrispondenzaLetta[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const [nome, setNome] = useState('');
  const [fornitore, setFornitore] = useState('');
  const [sigla, setSigla] = useState('');

  const ricarica = () => {
    listini
      .corrispondenze()
      .then(setRighe)
      .catch((e: Error) => setErrore(e.message));
  };

  useEffect(ricarica, []);

  const scrivi = (evento: React.FormEvent) => {
    evento.preventDefault();
    setInCorso(true);
    setErrore(null);

    listini
      .impostaCorrispondenza({
        nomeNelFile: nome,
        codiceFornitoreMago: fornitore,
        siglaProduttore: sigla,
      })
      .then(() => {
        setNome('');
        setFornitore('');
        setSigla('');
        ricarica();
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  /*  Si ricompila il modulo con una riga esistente invece di avere una modalità
      «modifica»: il nome nel file è la chiave, quindi riscriverlo con valori nuovi È la
      correzione. Una schermata con due modi avrebbe dovuto spiegare la differenza fra
      creare e correggere, che qui non esiste.  */
  const riprendi = (riga: CorrispondenzaLetta) => {
    setNome(riga.nomeNelFile);
    setFornitore(riga.codiceFornitoreMago);
    setSigla(riga.siglaProduttore);
  };

  return (
    <aside className="scheda">
      <header>
        <div>
          <h2>Corrispondenze</h2>
          <p className="dettaglio">Di chi è un nome trovato nei file</p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi">
          ×
        </button>
      </header>

      {errore !== null && <p className="errore">{errore}</p>}

      {modificabili && (
        <form className="blocco" onSubmit={scrivi}>
          <label>
            Nome nel file
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="COMINOX"
              required
            />
          </label>

          <label>
            Fornitore in Mago
            <input
              value={fornitore}
              onChange={(e) => setFornitore(e.target.value)}
              placeholder="101568"
              required
            />
          </label>

          <label>
            Sigla produttore
            <input
              value={sigla}
              onChange={(e) => setSigla(e.target.value.toUpperCase())}
              maxLength={3}
              minLength={3}
              placeholder="BRB"
              required
            />
          </label>

          <p className="nota">
            Tre caratteri, la sigla Metel. Vale per i file compilati sul nostro modello,
            dove la sigla non c'è; nel catalogo di un grossista la sigla sta nel file e
            questa non viene guardata — ma va scritta lo stesso.
          </p>

          <button type="submit" className="primario" disabled={inCorso}>
            {inCorso ? 'Salvataggio…' : 'Dichiara'}
          </button>
        </form>
      )}

      <section className="blocco">
        <h3>Dichiarate</h3>

        {righe === null && <p className="attesa">Lettura…</p>}
        {righe !== null && righe.length === 0 && (
          <p className="nota">
            Nessuna. Finché non ce n'è almeno una, ogni file si ferma in attesa che
            qualcuno dica di chi è.
          </p>
        )}

        {righe !== null && righe.length > 0 && (
          <table className="elenco">
            <thead>
              <tr>
                <th>Nel file</th>
                <th>Mago</th>
                <th>Sigla</th>
              </tr>
            </thead>
            <tbody>
              {righe.map((riga) => (
                <tr
                  key={riga.id}
                  onClick={modificabili ? () => riprendi(riga) : undefined}
                  className={modificabili ? 'cliccabile' : undefined}
                >
                  <td>{riga.nomeNelFile}</td>
                  <td>{riga.codiceFornitoreMago}</td>
                  <td>{riga.siglaProduttore}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {modificabili && righe !== null && righe.length > 0 && (
          <p className="nota">Una riga si corregge riscrivendola: cliccala per riprenderla.</p>
        )}
      </section>
    </aside>
  );
}
